import { useFormik } from "formik";
import { use, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { invoiceActions } from "../../sagas/invoiceSlice";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import {
    FaUser,
    FaBuilding,
    FaPlus,
    FaTrash,
    FaSave,
    FaIdCard,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaMoneyBillWave,
    FaCreditCard,
    FaWallet,
    FaBusinessTime,
    FaArrowLeft,
    FaShieldAlt,
    FaArrowUp,
    FaArrowDown,
    FaBalanceScale
} from 'react-icons/fa';
import Swal from "sweetalert2";
import { devisActions } from "../../sagas/devisSlice";
import { clientActions } from "../../sagas/clientSlice";
import Select from 'react-select';
import { productActions } from "../../sagas/productSlice";
import * as Yup from 'yup'; // Import Yup for validation
import DevisLine from "./DevisLine";
import { components } from 'react-select';
import { formatPrice } from "../../helpers/utility";
import { reglementActions } from "../../sagas/reglementSlice";

// Villes marocaines
const villesMaroc = [
    'Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger',
    'Agadir', 'Meknès', 'Oujda', 'Kénitra', 'Tétouan',
    'Safi', 'Mohammedia', 'El Jadida', 'Nador', 'Beni Mellal'
];

const initForm = {
    clientId: null,
    chantierId: null,
    lignes: [],
    dateExpiration: '',
    dateOperation: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
    validite: 30,
    remarques: '',
    totalAmountHt: 0,
    totalAmountTtc: 0,
    totalAmountTva: 0,
    statut: 'EN_ATTENTE',
    modeReglement: null
};

const DevisForm = ({ onClose, devis }) => {
    const { t } = useTranslation('translation');
    const [formState, setForm] = useState(initForm);
    const dispatch = useDispatch();
    const clients = useSelector(state => state.clients.clients);
    const products = useSelector(state => state.products.products);
    const [cachedOptions, setCachedOptions] = useState([]);
    const [showClientModal, setShowClientModal] = useState(false);
    const [clientErrors, setClientErrors] = useState({});
    const [selectedClient, setSelectedClient] = useState(null);
    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const [selectedChantier, setSelectedChantier] = useState(null);
    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });

    const canEdit = devis && devis.statut === 'EN_ATTENTE' || !devis;

    const [client, setClient] = useState({
        nom: '',
        prenom: '',
        cin: '',
        telephone1: '',
        telephone2: '',
        email: '',
        adresse: '',
        ville: 'Casablanca',
        typeClient: 'PARTICULIER',
        codePostal: '',
        plafond: 0,
        entreprise: '', // New field for "Entreprise"
        ice: '',// New field for "ICE",
        active: true,
        solde: 0 // Ce champ sera en lecture seule
    });


    console.log("devis dans form", devis);
    useEffect(() => {
        if (devis) {
            setForm({ ...devis });
        } else {
            setForm(initForm);
        }

        if (!clients || clients.length === 0) {
            dispatch(clientActions.search({
                query: ""
            }));
        }

    }, [devis, clients, dispatch]);

    // Validation schema
    const validationSchema = Yup.object().shape({
        clientId: Yup.number()
            .required(t('devis.validation.clientRequired')), // Ensure a client is selected
        lignes: Yup.array().min(1, t('devis.validation.atLeastOneItem')), // Ensure at least one item is added
    });

    const formik = useFormik({
        initialValues: {
            ...formState,
            clientId: devis ? devis.clientId : null,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            if (devis) {
                let payload = {
                    id: devis.id,
                    data: values,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.updated'),
                            text: t('devis.updatedSuccess'),
                        });
                        onClose();
                    },
                };
                dispatch(devisActions.updateDevisRequest(payload));
            } else {
                let payload = {
                    data: values,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.saved'),
                            text: t('devis.savedSuccess'),
                        });
                        onClose();
                    },
                };
                dispatch(devisActions.add(payload));
            }
        },
    });

    useEffect(() => {
        // Charger la liste des clients si elle n'est pas déjà disponible
        if (!clients || clients.length === 0) {
            dispatch(clientActions.search({
                query: ""
            }));
        }

        if (!products || products.length === 0) {
            dispatch(productActions.findAll({
                search: "",
                page: 0,
                size: 10,
            }));
        }

        if (clients && devis) {
            console.log("Setting form with devis:", devis);
            setForm({ ...devis });
            if (devis.chantierNom && devis.clientId) {
                const client = clients.find(c => c.id === devis.clientId);
                if (client) {
                    const chantier = client.chantiers.find(ch => ch.nom === devis.chantierNom);
                    setSelectedChantier(chantier ? { value: chantier.id, label: chantier.nom } : null);
                    setSelectedClient({
                        value: client.id,
                        label: client.typeClient === 'PARTICULIER' ? `${client.nom} ${client.prenom || ''}` : client.entreprise,
                        chantiers: client.chantiers,
                        solde: client.solde,
                        totalDebit: client.totalDebit,
                        totalCredit: client.totalCredit,
                    });

                }
            }
        }

    }, [devis, clients, dispatch]);


    const { values, getFieldProps, handleSubmit, setFieldValue, errors, touched } = formik;

    // Calculate montantTotalTaxe and montantTotalTTC
    const montantTotalTaxe = values.montantTotalTaxe || 0;
    const montantTotalTTC = values.montantTotalTTC || 0;
    const montantTotalHT = values.montantTotalHT || 0;

    console.log(montantTotalHT)

    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        chantiers: client.chantiers,
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
    })) ?? [];


    const handleClientChange = (e) => {
        const { name, value } = e.target;
        setClient((prev) => ({
            ...prev,
            [name]: name === 'active' ? value === 'true' : value, // Convert "true"/"false" to boolean for the active field
        }));
    };

    const handleCreateClient = () => {
        setShowClientModal(true);
    };

    const handleSubmitClient = (e) => {
        e.preventDefault();

        const newErrors = {};
        // Common validations
        if (!client.telephone1) newErrors.telephone1 = 'Téléphone obligatoire';
        if (!client.adresse) newErrors.adresse = 'Adresse obligatoire';

        // Validations for "PARTICULIER"
        if (client.typeClient === 'PARTICULIER') {
            if (!client.nom) newErrors.nom = 'Nom obligatoire';
            if (!client.prenom) newErrors.prenom = 'Prénom obligatoire';
        }

        // Validations for "ENTREPRISE"
        if (client.typeClient === 'ENTREPRISE') {
            if (!client.entreprise) newErrors.entreprise = 'Nom de l\'entreprise obligatoire';
            if (!client.ice) newErrors.ice = 'Numéro ICE obligatoire';
        }


        if (Object.keys(newErrors).length > 0) {
            setClientErrors(newErrors);
            return;
        }

        let payload = {
            data: {
                ...client,
                chantiers: [],
            },

            onSuccess: () => {
                Swal.fire({
                    icon: 'success',
                    title: 'Succès',
                    text: 'Client enregistrés avec succès!',
                    confirmButtonText: 'OK',
                });

                setTimeout(() => {
                    // Réinitialisation du formulaire après succès
                    setClient({
                        nom: '',
                        prenom: '',
                        cin: '',
                        telephone1: '',
                        telephone2: '',
                        typeClient: 'PARTICULIER',
                        email: '',
                        adresse: '',
                        ville: 'Casablanca',
                        codePostal: '',
                        plafond: 0,
                        entreprise: '',
                        ice: '',
                        solde: 0,
                        active: true,
                    });

                    // Fermeture du modal
                    setShowClientModal(false);
                }, 2000);
            },
        };

        dispatch(clientActions.addClient(payload));

    }

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    return (
        <Container fluid>
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <Card>
                    <CardHeader>
                        <Row>
                            <Col md="3">
                                <Label htmlFor="clientId">{t('bonLivraison.client')}:</Label>
                                <Select
                                    id="clientId"
                                    options={clientOptions}
                                    value={clientOptions.find(option => option.value === values.clientId)}
                                    onChange={(selectedOption) => {
                                        setFieldValue('clientId', selectedOption.value);
                                        setSelectedClient(selectedOption);
                                        dispatch(reglementActions.echeanceClient({ ...filters, clientId: selectedOption.value }));
                                        dispatch(reglementActions.cautionClient({ ...filters, clientId: selectedOption.value }));
                                        setFilters(f => ({ ...f, clientId: selectedOption.value }));
                                    }}
                                    placeholder={t('bonLivraison.selectClient')}
                                    isSearchable
                                    isDisabled={devis && devis.statut === 'VALIDE' ? true : false}
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                                {errors.clientId && touched.clientId && (
                                    <div className="text-danger small mt-1">{errors.clientId}</div>
                                )}
                            </Col>
                            
                            <Col md="2">
                                <Label for="validite">{t('devis.validite')}</Label>
                                <Input
                                    type="select"
                                    name="validite"
                                    id="validite"
                                    value={values.validite || 30}
                                    onChange={(e) => setFieldValue('validite', e.target.value)}
                                    disabled={!canEdit}
                                >
                                    <option value={15}>15 {t('devis.jours')}</option>
                                    <option value={30}>30 {t('devis.jours')}</option>
                                    <option value={45}>45 {t('devis.jours')}</option>
                                    <option value={60}>60 {t('devis.jours')}</option>
                                    <option value={90}>90 {t('devis.jours')}</option>
                                </Input>
                            </Col>
                            <Col md="2">
                                <Label htmlFor="dateOperation">{t('commande.dateOperation')}:</Label>
                                <Input
                                    type="date"
                                    id="dateOperation"
                                    {...getFieldProps('dateOperation')}
                                    className="form-control"
                                    disabled={!canEdit}
                                />
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label for="modeReglement">
                                        <FaMoneyBillWave className="me-1" />
                                        {t('facture.modeReglement')} *
                                    </Label>
                                    <Input
                                        type="select"
                                        id="modeReglement"
                                        name="modeReglement"
                                        value={values.modeReglement}
                                        onChange={(e) => setFieldValue('modeReglement', e.target.value)}
                                        disabled={!canEdit}
                                    >
                                        <option value="">{t('common.select')}</option>
                                        <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                        <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                        <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                        <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                        <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        {selectedClient && (
                            <Row className="mt-2 g-2">
                                <Col>
                                    <Row className="g-2 align-items-stretch">
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaBalanceScale className="mb-1 text-success" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.balance')}</span>
                                                    {selectedClient.totalCredit > selectedClient.totalDebit ? (
                                                        <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>+{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : selectedClient.totalCredit < selectedClient.totalDebit ? (
                                                        <span className="fw-bold text-danger" style={{ fontSize: '0.9rem' }}>-{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                    ) : (
                                                        <span className="fw-bold text-muted" style={{ fontSize: '0.9rem' }}>{formatPrice(0)}</span>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaArrowDown className="mb-1 text-danger" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalDebit')}</span>
                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedClient.totalDebit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <FaArrowUp className="mb-1 text-primary" size={20} />
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalCredit')}</span>
                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedClient.totalCredit)}</span>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <Card className="shadow-sm h-100 bg-warning bg-opacity-10 border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <FaCalendarAlt className="me-1 text-warning" size={16} />
                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.echeances')}</span>
                                                    </div>
                                                    <span className="fw-bold text-warning" style={{ fontSize: '0.9rem' }}>{echeances?.length || 0}</span>
                                                    <Button color="link" size="sm" className="p-0 fw-bold text-warning" style={{ fontSize: '0.7rem' }} onClick={() => setShowEcheanceModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <Card className="shadow-sm h-100 bg-info bg-opacity-10 border-0 rounded-2">
                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <FaShieldAlt className="me-1 text-info" size={16} />
                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.cautions')}</span>
                                                    </div>
                                                    <span className="fw-bold text-info" style={{ fontSize: '0.9rem' }}>{cautions?.length || 0}</span>
                                                    <Button color="link" size="sm" className="p-0 fw-bold text-info" style={{ fontSize: '0.7rem' }} onClick={() => setShowCautionModal(true)}>
                                                        {t('client.viewDetails')}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col md="12">
                                <Label htmlFor="remarques">{t('devis.remarques')}:</Label>
                                <Input
                                    type="textarea"
                                    id="remarques"
                                    {...getFieldProps('remarques')}
                                    className="form-control"
                                    disabled={!canEdit}
                                />
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <DevisLine formik={formik} disabled={!canEdit} />
                        </Row>
                        {errors.lignes && touched.lignes && (
                            <div className="text-danger small mt-1">{errors.lignes}</div>
                        )}
                        <Row className="mt-3 justify-content-end">
                            <Col md="4" className="text-end">
                                <div className="mb-2">
                                    <h5>{t('devis.totalHT')}: {montantTotalHT} DH</h5>
                                </div>
                                <div className="mb-2">
                                    <h5>{t('devis.totalTaxe')}: {montantTotalTaxe} DH</h5>
                                </div>
                                <div>
                                    <h5>{t('devis.totalTTC')}: {montantTotalTTC} DH</h5>
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Button type="submit" color="primary" className="me-2" disabled={devis && devis.statut === 'VALIDE' ? true : false}>
                                    {t('common.save')}
                                </Button>
                                <Button type="button" color="secondary" onClick={onClose}>
                                    {t('common.cancel')}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Form>
            <Modal isOpen={showClientModal} toggle={() => setShowClientModal(false)} size="xl">
                <ModalHeader toggle={() => setShowClientModal(false)}>
                    {t('client.newClient')}
                </ModalHeader>
                <ModalBody>
                    <Card className="border-0 shadow-sm">
                        <CardBody className="p-2">
                            <Row>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="typeClient" className="fw-bold">
                                            <FaIdCard className="me-2 text-muted" />
                                            Type de Client*
                                        </Label>
                                        <Input
                                            type="select"
                                            id="typeClient"
                                            name="typeClient"
                                            value={client.typeClient}
                                            onChange={handleClientChange}
                                            className="py-2"
                                        >
                                            <option value="PARTICULIER">Particulier</option>
                                            <option value="ENTREPRISE">Entreprise</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>
                            {client.typeClient === 'PARTICULIER' &&
                                <Row>
                                    <Col md={3}>
                                        <FormGroup className="mb-4">
                                            <Label for="nom" className="fw-bold">
                                                <FaUser cclassName="me-2 text-muted" />
                                                Nom*
                                            </Label>
                                            <Input
                                                type="text"
                                                id="nom"
                                                name="nom"
                                                value={client.nom}
                                                onChange={handleClientChange}
                                                invalid={!!clientErrors.nom}
                                                placeholder="El Fassi"
                                                className="py-2"
                                            />
                                            {clientErrors.nom && <div className="text-danger small mt-1">{clientErrors.nom}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup className="mb-4">
                                            <Label for="prenom" className="fw-bold">
                                                <FaUser className="me-2 text-muted" />
                                                Prénom*
                                            </Label>
                                            <Input
                                                type="text"
                                                id="prenom"
                                                name="prenom"
                                                value={client.prenom}
                                                onChange={handleClientChange}
                                                invalid={!!clientErrors.prenom}
                                                placeholder="Karim"
                                                className="py-2"
                                            />
                                            {clientErrors.prenom && <div className="text-danger small mt-1">{clientErrors.prenom}</div>}
                                        </FormGroup>
                                    </Col>

                                    <Col md={3}>
                                        <FormGroup className="mb-4">
                                            <Label for="cin" className="fw-bold">
                                                <FaIdCard className="me-2 text-muted" />
                                                CIN
                                            </Label>
                                            <Input
                                                type="text"
                                                id="cin"
                                                name="cin"
                                                value={client.cin}
                                                onChange={handleClientChange}
                                                placeholder="AB123456"
                                                className="py-2"
                                            />
                                        </FormGroup>
                                    </Col>

                                </Row>}
                            {client.typeClient === 'ENTREPRISE' &&
                                <Row>
                                    <Col md={3}>
                                        <FormGroup className="mb-4">
                                            <Label for="entreprise" className="fw-bold">
                                                <FaBuilding className="me-2 text-muted" />
                                                Nom de l'entreprise
                                            </Label>
                                            <Input
                                                type="text"
                                                id="entreprise"
                                                name="entreprise"
                                                value={client.entreprise}
                                                onChange={handleClientChange}
                                                placeholder="Nom de l'entreprise"
                                                className="py-2"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup className="mb-4">
                                            <Label for="ice" className="fw-bold">
                                                <FaIdCard className="me-2 text-muted" />
                                                ICE
                                            </Label>
                                            <Input
                                                type="text"
                                                id="ice"
                                                name="ice"
                                                value={client.ice}
                                                onChange={handleClientChange}
                                                placeholder="Numéro ICE"
                                                className="py-2"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>}
                            <Row>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="telephone" className="fw-bold">
                                            <FaPhone className="me-2 text-muted" />
                                            Téléphone (1) *
                                        </Label>
                                        <Input
                                            type="tel"
                                            id="telephone1"
                                            name="telephone1"
                                            value={client.telephone1}
                                            onChange={handleClientChange}
                                            invalid={!!clientErrors.telephone1}
                                            placeholder="06 12 34 56 78"
                                            className="py-2"
                                        />
                                        {clientErrors.telephone1 && <div className="text-danger small mt-1">{clientErrors.telephone1}</div>}
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="telephone2" className="fw-bold">
                                            <FaPhone className="me-2 text-muted" />
                                            Téléphone (2) *
                                        </Label>
                                        <Input
                                            type="tel"
                                            id="telephone2"
                                            name="telephone2"
                                            value={client.telephone2}
                                            onChange={handleClientChange}
                                            invalid={!!clientErrors.telephone2}
                                            placeholder="06 12 34 56 78"
                                            className="py-2"
                                        />
                                        {clientErrors.telephone2 && <div className="text-danger small mt-1">{clientErrors.telephone2}</div>}
                                    </FormGroup>
                                </Col>

                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="email" className="fw-bold">
                                            <FaEnvelope className="me-2 text-muted" />
                                            Email
                                        </Label>
                                        <Input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={client.email}
                                            onChange={handleClientChange}
                                            placeholder="k.elfassi@example.com"
                                            className="py-2"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="ville" className="fw-bold">
                                            <FaMapMarkerAlt className="me-2 text-muted" />
                                            Ville
                                        </Label>
                                        <Input
                                            type="select"
                                            id="ville"
                                            name="ville"
                                            value={client.ville}
                                            onChange={handleClientChange}
                                            className="py-2"
                                        >
                                            {villesMaroc.map(ville => (
                                                <option key={ville} value={ville}>{ville}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>

                            </Row>
                            <Row>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="adresse" className="fw-bold">
                                            <FaMapMarkerAlt className="me-2 text-muted" />
                                            Adresse*
                                        </Label>
                                        <Input
                                            type="textarea"
                                            rows="2"
                                            id="adresse"
                                            name="adresse"
                                            value={clientErrors.adresse}
                                            onChange={handleClientChange}
                                            invalid={!!clientErrors.adresse}
                                            placeholder="12 Rue Moulay Rachid"
                                            className="py-2"
                                        />
                                        {clientErrors.adresse && <div className="text-danger small mt-1">{clientErrors.adresse}</div>}
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-4">
                                        <Label for="codePostal" className="fw-bold">
                                            <FaMapMarkerAlt className="me-2 text-muted" />
                                            Code Postal
                                        </Label>
                                        <Input
                                            type="text"
                                            id="codePostal"
                                            name="codePostal"
                                            value={client.codePostal}
                                            onChange={handleClientChange}
                                            placeholder="20000"
                                            className="py-2"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleSubmitClient}>
                        {t('common.save')}
                    </Button>
                    <Button color="secondary" onClick={() => setShowClientModal(false)}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};
export default DevisForm;